import { Card, Col, Form, InputNumber, Row, Typography } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import { ConditionsFieldInput } from 'components/atoms/ConditionsFieldInput';
import { DateInput } from 'components/UI/FormItems';
import { CurrencyInput } from 'components/UI/FormItems/CurrencyInput';
import { SelectInput } from 'components/UI/FormItems/SelectInput';
import { useField } from 'formik';
import { PriceBookEntryLabels } from 'models/PriceBookEntry';
import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { useGetProductCatalogItemsQuery } from 'redux/services/julia/juliaApi';
import { FormLabel } from 'components/UI/FormItems/FormLabel';

interface Props {
  isDisabled: boolean;
  isProductNumberLocked?: boolean;
}

const DiscountPercentageInput: FC<{ fieldName: string; label: string; disabled?: boolean }> = ({ fieldName, label, disabled }) => {
  const [{ value }, { touched, error }, { setValue, setTouched }] = useField<number>(fieldName);

  return (
    <Form.Item
      labelAlign="left"
      validateStatus={touched && error ? 'error' : 'success'}
      label={<FormLabel label={label} />}
      help={touched && error ? error : undefined}
      style={{ marginBottom: 0 }}
      labelCol={{ style: { padding: 0 } }}>
      <InputNumber
        disabled={disabled}
        value={value || undefined}
        onChange={(val): void => setValue(Number(val))}
        onFocus={(): void => setTouched(false)}
        onBlur={(): void => setTouched(true)}
        style={{ borderRadius: 5, width: '100%' }}
        controls={false}
        min={0}
        max={100}
        formatter={(val): string => `${val}%`}
        parser={(val): number => Number((val || '').replace('%', ''))}
      />
    </Form.Item>
  );
};

const typeOptions: DefaultOptionType[] = ['Standard', 'Promotion'].map((value) => ({ label: value, value }));

export const PriceBookEntryForm: FC<Props> = ({ isDisabled, isProductNumberLocked }) => {
  const { id: businessId } = useParams();

  const { data: catalogItemsResponse, isLoading: isCatalogLoading } = useGetProductCatalogItemsQuery(
    { customerId: businessId || '', params: { offset: 0, take: 500, includeInactiveData: true } },
    { skip: !businessId }
  );

  const productNumberOptions: DefaultOptionType[] | undefined = catalogItemsResponse?.data?.map((item) => ({
    value: item.productNumber?.trim(),
    label: item.productNumber?.trim()
  }));

  return (
    <Form layout="vertical">
      <Row gutter={[10, 10]}>
        <Col span={8}>
          <Card headStyle={{ background: 'rgb(206, 213, 242)' }} title={<Typography.Title level={4}>Product Info</Typography.Title>}>
            <SelectInput
              fieldName="productNumber"
              label={PriceBookEntryLabels.productNumber}
              options={productNumberOptions}
              loading={isCatalogLoading}
              disabled={isDisabled || isProductNumberLocked}
              showSearch
              filterOption={(input, option): boolean => {
                if (typeof option?.value !== 'number') return (option?.value as string)?.toLowerCase().includes(input.toLowerCase());

                return false;
              }}
            />
            <ConditionsFieldInput fieldName="condition" label={PriceBookEntryLabels.condition} />
            <SelectInput fieldName="type" label={PriceBookEntryLabels.type} options={typeOptions} disabled={isDisabled} />
          </Card>
        </Col>
        <Col span={8}>
          <Card headStyle={{ background: '#18a79984' }} title={<Typography.Title level={4}>Pricing</Typography.Title>}>
            <CurrencyInput fieldName="listPrice" label={PriceBookEntryLabels.listPrice} disabled={isDisabled} />
            <CurrencyInput fieldName="unitPrice" label={PriceBookEntryLabels.unitPrice} disabled={isDisabled} />
            <DiscountPercentageInput fieldName="discountPercentage" label={PriceBookEntryLabels.discountPercentage} disabled={isDisabled} />
          </Card>
        </Col>
        <Col span={8}>
          <Card headStyle={{ background: '#b0b2bc' }} title={<Typography.Title level={4}>Schedule & Status</Typography.Title>}>
            <DateInput fieldName="effectiveDate" label={PriceBookEntryLabels.effectiveDate} />
            <DateInput fieldName="endDate" label={PriceBookEntryLabels.endDate} />
          </Card>
        </Col>
      </Row>
    </Form>
  );
};
